import { ref } from 'vue'
import { SetRequired } from 'type-fest'
import type { TransportationUIInput } from '@/types/transportation'
import { useContext } from '@/composables/context'

const TransportationValidationErrors = [
  'missingOrigin',
  'missingDestination',
  'missingBothLocations',
  'invalidDepartureIATACode',
  'invalidArrivalIATACode',
] as const

export type TransportationValidationError =
  (typeof TransportationValidationErrors)[number]

/**
 * Composable for managing transportation form validation
 * Provides centralized validation state.
 */
export function useTransportationValidation() {
  // Validation trigger state
  const isValidating = ref(false)
  const errors = ref<Map<TransportationValidationError, string>>(new Map())

  const { i18n } = useContext()

  /**
   * Validates a transportation object
   * @param transportation - The transportation to validate
   * @returns Object with validation results
   */
  function validateTransportation(
    transportation: TransportationUIInput,
  ): transportation is SetRequired<
    TransportationUIInput,
    'origin' | 'destination'
  > {
    // TODO: localize error messages
    isValidating.value = true

    // Create a new Map to ensure reactivity
    const newErrors = new Map<TransportationValidationError, string>()

    // Location validation
    if (!transportation.origin && !transportation.destination) {
      newErrors.set(
        'missingBothLocations',
        i18n.t('transportation.validation.missingBothLocations').toString(),
      )
    }
    if (!transportation.origin) {
      newErrors.set(
        'missingOrigin',
        i18n.t('transportation.validation.missingOrigin').toString(),
      )
    }
    if (!transportation.destination) {
      newErrors.set(
        'missingDestination',
        i18n.t('transportation.validation.missingDestination').toString(),
      )
    }

    // Additional IATA validation for flight transportation
    if (transportation.type === 'flight') {
      if (
        transportation.departureAirport.iata &&
        transportation.departureAirport.iata.trim().length !== 3
      ) {
        newErrors.set(
          'invalidDepartureIATACode',
          i18n
            .t('transportation.validation.invalidDepartureIATACode')
            .toString(),
        )
      }
      if (
        transportation.arrivalAirport.iata &&
        transportation.arrivalAirport.iata.trim().length !== 3
      ) {
        newErrors.set(
          'invalidArrivalIATACode',
          i18n.t('transportation.validation.invalidArrivalIATACode').toString(),
        )
      }
    }

    // Update the reactive ref with the new Map otherwise vue does not detect
    // changes
    errors.value = newErrors
    isValidating.value = false

    return newErrors.size === 0
  }

  return {
    errors,
    isValidating,
    validateTransportation,
  }
}
